import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  COUNTERPARTY_NAME_FIELD,
  type VerificationQueueItem,
  type VerifyFieldOutcome,
  type VerifyFieldResponse,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { verifyField } from '../api/verifications';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ErrorNotice } from '../components/ErrorNotice';
import { OpenContractLink } from '../components/OpenContractLink';
import { ProvenanceDetail } from '../components/ProvenanceChip';
import { TRUST_LABELS, verificationStatusLabel } from '../components/trust-labels';
import { VerificationBadge } from '../components/VerificationBadge';
import { RETIRED_COPY, askedWithCopy, isAdminFieldName, overBoundCopy, tablesMissingApplies } from './extraction-fields-shared';

/**
 * The field detail panel — where the human half of the CR-01 evidence chain is
 * created.
 *
 * ## Three controls, not one
 *
 * `VerifyFieldOutcomeSchema` has three members and they stay three controls
 * here. NFR-12 measures extraction accuracy by the **correction rate**, and a
 * single "Verify" button that quietly accepted a changed value would destroy
 * the only accuracy signal this system generates for free. Correct also carries
 * an explicit *"the contract does not state one"* which sends
 * `confirmed_value: null` — a distinct and useful correction, because it tells
 * the extractor it hallucinated a value rather than mis-read one. Reject
 * requires a reason, and a rejected field stays visible and keeps escalating; a
 * field that vanished from the queue because nobody could read it is precisely
 * how FR-07 fails.
 *
 * ## `seen_extracted_value` is what was on screen
 *
 * Sent as the optimistic-concurrency token, and it is read from the item the
 * reviewer was actually shown — never re-fetched, never recomputed. If
 * re-extraction changed the value since this queue was rendered, the server
 * answers `409` rather than recording an attestation to a value nobody read.
 *
 * ## A 409 is not an error
 *
 * It means another reviewer settled the field first. It renders as the neutral
 * "another reviewer settled this field" notice with a Reload-this-row action —
 * see ErrorNotice — and the row is *not* marked verified. Styling that red would
 * teach reviewers that a normal outcome is a fault.
 *
 * ## An `AutoApproved` row is view-only (CLM-108, R-108-17)
 *
 * The queue opens on settled-by-policy rows only from the Auto-approval page's
 * link (`?status=AutoApproved`). Such a row renders **no verbs** — the API
 * refuses them anyway (R-108-21) — and a line saying it was settled by policy
 * and not reviewed by a person. The receipt of an ordinary settle names the
 * contract's **trust** (`contract_data_trust`), so the last human field settled
 * beside auto-approved ones reads "Settled — includes auto-approved fields",
 * never "Verified" (D-2.1-4).
 *
 * ## `parties.primary.counterparty_name` — provided vs found in the document (CLM-78)
 *
 * The hold writes this row when the in-tenant leak scan finds the counterparty
 * spelled differently from the party's known names (R-76-2, B4). No model saw
 * it: `origin = 'system_derived'`. Two cards — **Provided** (the party named at
 * ingest, `primary_party_display_name`) and **In the document**
 * (`extracted_value`) — and four verbs: *Confirm provided* (the spelling is this
 * party; the alias is approved so FR-29 substitutes it, the display name stays),
 * *Accept extracted* (alias approved **and**, for a CLM-registered vendor only,
 * the display name becomes the found spelling — A5, DR-04), *Correct* (a typed
 * name becomes a confirmed alias; the detected one is rejected) and *Reject*
 * (not this party; the contract stays held — A4). Every verb but Reject re-queues
 * the held contract for pseudonymisation with the new alias (rule 2).
 *
 * The gate is `party:manage_alias` (admin ∪ Legal, A2). The role check here is
 * a usability hint only — the verbs are disabled with the copy *"Legal or an
 * admin decides this row"*; the API refuses a contributor with 403 whatever this
 * component renders (NFR-06). It lives in {@link AliasDeciderGate}, mounted
 * only for this field, so every other row renders without touching MSAL. A row
 * a human already settled is never replaced by a later hold with a different
 * spelling (`uq_field_verification_field`); that later spelling still appears on
 * the Party aliases surface.
 */

/** The roles the gateway grants `party:manage_alias` to — a display hint, never the gate. */
const ALIAS_DECIDER_ROLES: readonly string[] = ['admin', 'legal_reviewer'];

/**
 * Reads the signed-in roles once, for the counterparty-name row only.
 * `mayAttempt` unions the app-role claim with the group-derived roles and
 * stays true when the sign-in names no role at all (D-82-1) — the verbs are
 * then enabled and the API's 403 is the answer.
 */
function AliasDeciderGate({ render }: { readonly render: (mayDecide: boolean) => ReactNode }) {
  const { identity } = useIdentity();
  const mayDecide = mayAttempt(identity, ALIAS_DECIDER_ROLES);
  return <>{render(mayDecide)}</>;
}

type PanelPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'settled'; readonly response: VerifyFieldResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

type Draft = { readonly kind: 'none' } | { readonly kind: 'correct' } | { readonly kind: 'reject' };

export function VerifyPanel({
  item,
  onClose,
  onSettled,
}: {
  readonly item: VerificationQueueItem;
  readonly onClose: () => void;
  /** Called once the server has recorded an outcome, so the queue can refresh. */
  readonly onSettled: () => void;
}) {
  const [phase, setPhase] = useState<PanelPhase>({ kind: 'idle' });
  const [draft, setDraft] = useState<Draft>({ kind: 'none' });
  const [correctedValue, setCorrectedValue] = useState('');
  const [statesNoValue, setStatesNoValue] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  /* CLM-78 — the counterparty-name row is an alias decision (A2); everything else renders as before. */
  const isCounterpartyName = item.field_name === COUNTERPARTY_NAME_FIELD;
  const updatesDisplayName = item.primary_party_sor_system === 'CLM';

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Focus moves to the panel heading on open, so a keyboard or screen-reader
  // user is put where the new content is rather than being left on the row.
  useEffect(() => {
    headingRef.current?.focus();
    setPhase({ kind: 'idle' });
    setDraft({ kind: 'none' });
    setCorrectedValue('');
    setStatesNoValue(false);
    setRejectReason('');
    setFieldError(null);
  }, [item.verification_id]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  function submit(outcome: VerifyFieldOutcome) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setFieldError(null);
    setPhase({ kind: 'submitting' });

    void (async () => {
      try {
        const response = await verifyField(
          {
            verification_id: item.verification_id,
            contract_key: item.contract_key,
            field_name: item.field_name,
            outcome,
            // Exactly what was rendered. See the module note.
            seen_extracted_value: item.extracted_value,
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setPhase({ kind: 'settled', response });
        onSettled();
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();
  }

  function onCorrect() {
    if (statesNoValue && !isCounterpartyName) {
      submit({ outcome: 'correct', confirmed_value: null });
      return;
    }
    const value = correctedValue.trim();
    if (value === '' && isCounterpartyName) {
      // A counterparty always has a name; the API refuses an empty correction on this row.
      setFieldError('Type the counterparty’s name as it should be known — it becomes a confirmed alias.');
      return;
    }
    if (value === '') {
      setFieldError(
        'Enter the value the contract states, or tick “the contract does not state one” — which is itself a correction worth recording.',
      );
      return;
    }
    submit({ outcome: 'correct', confirmed_value: value });
  }

  function onReject() {
    const reason = rejectReason.trim();
    if (reason === '') {
      setFieldError(
        'A rejection needs a reason. The field stays in the queue and keeps escalating, so the next reviewer needs to know what stopped this one.',
      );
      return;
    }
    submit({ outcome: 'reject', reason });
  }

  const submitting = phase.kind === 'submitting';
  const settled = phase.kind === 'settled';
  /*
   * CLM-108 (R-108-17, R-108-21) — a row the policy settled is terminal: the
   * API refuses any verb on it (409, before any write), so the panel offers
   * none. It is shown so a reviewer can see what was approved and when; sending
   * it back for review is a follow-on story (Q-108-2), not a hidden verb here.
   */
  const autoApproved = item.verification_status === 'AutoApproved';
  const verification = item.provenance.verification;
  const autoApprovedUtc =
    item.auto_approved_utc ??
    (!verification.is_human_verified && verification.verification_status === 'AutoApproved' ? verification.auto_approved_utc : null);

  /** The verbs and their drafts; `mayDecide` is the display hint for the counterparty row. */
  function controls(mayDecide: boolean): ReactNode {
    const verbsDisabled = submitting || !mayDecide;
    return (
      <>
        {isCounterpartyName && !mayDecide ? (
          <p className="mut" role="note" data-testid="alias-gate-note">
            Legal or an admin decides this row — it changes what FR-29 substitutes before any text leaves the
            tenant.
          </p>
        ) : null}
        <div className="vacts">
          <button
            type="button"
            className="btn pri"
            disabled={verbsDisabled}
            onClick={() => {
              submit({ outcome: 'confirm' });
            }}
          >
            {isCounterpartyName ? 'Confirm provided' : 'Confirm the extracted value'}
          </button>
          {isCounterpartyName ? (
            <button
              type="button"
              className="btn"
              disabled={verbsDisabled || item.extracted_value === null}
              title={
                updatesDisplayName
                  ? 'Approves the found spelling as an alias and makes it the vendor’s display name (CLM-registered vendor).'
                  : 'Approves the found spelling as an alias; the display name stays as the source system holds it.'
              }
              onClick={() => {
                submit({ outcome: 'accept_extracted' });
              }}
            >
              Accept extracted
              {updatesDisplayName ? <span className="mut"> — updates the vendor’s display name</span> : null}
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            disabled={verbsDisabled}
            aria-expanded={draft.kind === 'correct'}
            onClick={() => {
              setDraft(draft.kind === 'correct' ? { kind: 'none' } : { kind: 'correct' });
              setFieldError(null);
            }}
          >
            {isCounterpartyName ? 'Correct' : 'Correct it'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={verbsDisabled}
            aria-expanded={draft.kind === 'reject'}
            onClick={() => {
              setDraft(draft.kind === 'reject' ? { kind: 'none' } : { kind: 'reject' });
              setFieldError(null);
            }}
          >
            {isCounterpartyName ? 'Reject' : 'Reject — cannot be settled'}
          </button>
          <button type="button" className="btn qt" onClick={onClose}>
            Close
          </button>
        </div>

        {draft.kind === 'correct' ? (
          <div className="vform">
            {isCounterpartyName ? (
              <p>
                The typed name becomes a <strong>confirmed alias</strong> of this party (FR-29 substitutes it
                from the next run); the spelling found in the document is rejected
                {updatesDisplayName ? ', and the vendor’s display name becomes the typed name' : ''}. The
                contract is re-queued.
              </p>
            ) : (
              <p>
                A correction is recorded as <code>Corrected</code> rather than <code>Verified</code>, and the
                two are deliberately kept apart: the correction rate is how extraction accuracy is measured
                (NFR-12).
              </p>
            )}
            <div className="field">
              <label htmlFor="vq-corrected">
                {isCounterpartyName ? 'The counterparty’s name' : 'The value the contract states'}
              </label>
              <input
                id="vq-corrected"
                type="text"
                maxLength={400}
                autoComplete="off"
                value={correctedValue}
                disabled={submitting || statesNoValue}
                onChange={(event) => setCorrectedValue(event.target.value)}
              />
            </div>
            {isCounterpartyName ? null : (
              <label className="vabsent">
                <input
                  type="checkbox"
                  checked={statesNoValue}
                  disabled={submitting}
                  onChange={(event) => setStatesNoValue(event.target.checked)}
                />
                The contract does not state one
              </label>
            )}
            <button type="button" className="btn pri" disabled={submitting} onClick={onCorrect}>
              Record the correction
            </button>
          </div>
        ) : null}

        {draft.kind === 'reject' ? (
          <div className="vform">
            {isCounterpartyName ? (
              <p>
                Reject means <strong>the document names a different party</strong>: the found spelling is
                rejected as an alias and the contract stays held — re-filing it against the right party is a
                separate step.
              </p>
            ) : (
              <p>
                A rejection is not a verification. The field stays visible and escalation continues — this
                records <em>why</em> it could not be settled from this document.
              </p>
            )}
            <div className="field">
              <label htmlFor="vq-reason">Why this field cannot be settled</label>
              <textarea
                id="vq-reason"
                rows={3}
                maxLength={1000}
                value={rejectReason}
                disabled={submitting}
                onChange={(event) => setRejectReason(event.target.value)}
              />
            </div>
            <button type="button" className="btn pri" disabled={submitting} onClick={onReject}>
              Record the rejection
            </button>
          </div>
        ) : null}

        {fieldError === null ? null : (
          <p className="ferr" role="alert">
            {fieldError}
          </p>
        )}
      </>
    );
  }

  return (
    <section className="vpanel" aria-labelledby="vpanel-heading">
      {/*
       * tabIndex={-1} so focus can be moved here programmatically without the
       * heading becoming a tab stop of its own.
       */}
      <h3 id="vpanel-heading" ref={headingRef} tabIndex={-1}>
        {item.field_name}
      </h3>
      <p className="vsub">
        <code>{item.contract_key}</code> · {item.primary_party_display_name} ·{' '}
        {item.is_high_stakes ? 'high stakes' : 'standard'}{' '}
        {/* CLM-89 (R-89-1) — read the PDF before confirming or correcting. */}
        <OpenContractLink url={item.sharepoint_item_url ?? null} />
      </p>
      {/* CLM-109 (R-109-15) — a reviewer cannot judge a value for a field whose definition they cannot see:
          the description the model was ASKED WITH, which may differ from today's. */}
      {isAdminFieldName(item.field_name) && item.field_description != null ? (
        <p className="mut" data-testid="admin-field-description">
          {askedWithCopy(item.field_description)}
        </p>
      ) : null}
      {item.field_retired === true ? (
        <p className="mut" data-testid="admin-field-retired">
          <span className="tag out">retired</span> {RETIRED_COPY}
        </p>
      ) : null}
      {/* CLM-109 (R-109-12) — the empty value is not "not stated": the model returned more than the bound. */}
      {item.field_over_bound_length != null ? (
        <p className="banner bad" role="status" data-testid="admin-field-over-bound">
          {overBoundCopy(item.field_over_bound_length)}
        </p>
      ) : null}
      {/* CLM-85 (D-85-8) — the caveat informs the decision, it does not take it: the verbs below stay enabled.
          CLM-109 extends it from key_terms.* to the admin-defined additional_terms.* rows. */}
      {tablesMissingApplies(item) ? (
        <p className="mut" data-testid="tables-missing-hint">
          Tables not detected in this document — the converter found no table although the text was fine, so this
          value may have been read from a flattened rate card. Check it against the PDF before accepting.
        </p>
      ) : null}

      {isCounterpartyName ? (
        /* CLM-78 — provided vs found in the document. */
        <div className="vcards" data-testid="counterparty-cards">
          <div className="vcard">
            <h4>Provided</h4>
            <p className="vval">{item.primary_party_display_name}</p>
            <p className="mut">The counterparty named when the contract was filed.</p>
          </div>
          <div className="vcard">
            <h4>In the document</h4>
            <p className="vval" data-absent={item.extracted_value === null ? 'true' : 'false'}>
              {item.extracted_value ?? 'No spelling was recorded.'}
            </p>
            <p className="mut">
              Found by the in-tenant leak scan while pseudonymising — no model saw it. The contract is held
              until this is decided.
            </p>
          </div>
        </div>
      ) : (
        <p className="vval" data-absent={item.extracted_value === null ? 'true' : 'false'}>
          {/* CLM-109 (R-109-12) — a contained value is null too, but "found no value" is the "not stated" reading
              the over-bound banner above exists to prevent; the queue row drops the sentence for the same reason. */}
          {item.extracted_value ??
            (item.field_over_bound_length != null
              ? 'No value was kept — the model’s answer was over this field’s bound.'
              : 'The pipeline found no value for this field.')}
        </p>
      )}

      <ProvenanceDetail provenance={item.provenance} />
      <p>
        <VerificationBadge verification={item.provenance.verification} />
      </p>

      {item.rejection_reason === null ? null : (
        <p className="mut">
          A previous reviewer rejected this field: {item.rejection_reason}
        </p>
      )}

      {autoApproved ? (
        <div className="vacts" data-testid="auto-approved-note">
          <p className="mut" role="note">
            Settled by policy at{' '}
            {autoApprovedUtc === null ? 'an unrecorded time' : <time dateTime={autoApprovedUtc}>{autoApprovedUtc}</time>} —
            not reviewed by a person. The field&rsquo;s measured accuracy met a co-signed policy (CR-06); the evidence
            is the policy&rsquo;s own audit row, not a verification, and the value stays as extracted.
          </p>
          <button type="button" className="btn qt" onClick={onClose}>
            Close
          </button>
        </div>
      ) : settled ? null : isCounterpartyName ? (
        <AliasDeciderGate render={controls} />
      ) : (
        controls(true)
      )}

      {/*
       * The outcome is announced politely rather than interrupting: a reviewer
       * working down a queue by keyboard hears it after their own keystroke,
       * not on top of it.
       */}
      <div className="voutcome" aria-live="polite">
        {phase.kind === 'submitting' ? <p>Recording your verification…</p> : null}

        {phase.kind === 'settled' ? (
          <div className="banner up" role="status">
            <p className="notice-head">
              <strong>Recorded as {verificationStatusLabel(phase.response.verification.verification_status)}</strong>
            </p>
            {/*
             * CLM-108 (D-2.1-4) — the contract's state is its TRUST, read from
             * `contract_data_trust`, never from `contract_processing_status`:
             * settling the last human field beside auto-approved ones takes the
             * pipeline to its end (`Verified`) while the contract is
             * `AutoApproved`, and this line must not announce "Verified" then.
             */}
            <p>
              The value now held is{' '}
              {phase.response.effective_value === null ? (
                <em>none — the contract does not state one</em>
              ) : (
                <code>{phase.response.effective_value}</code>
              )}
              . {phase.response.remaining_pending_count} field
              {phase.response.remaining_pending_count === 1 ? '' : 's'} on this contract still
              awaiting verification; this contract is now{' '}
              <em data-testid="receipt-trust">{TRUST_LABELS[phase.response.contract_data_trust]}</em>
              {isCounterpartyName && phase.response.contract_processing_status === 'Converted'
                ? ' — re-queued; the pipeline pseudonymises it again with the decided alias on its next run'
                : ''}
              .
            </p>
            <p className="mut">
              Audit row <code>{phase.response.audit.audit_id}</code> at{' '}
              {phase.response.audit.occurred_at_utc}
              {phase.response.audit.extraction_schema_version === null
                ? ''
                : `, extraction schema ${phase.response.audit.extraction_schema_version}`}
              . That row and this verification together are the evidence for this field.
            </p>
            <button type="button" className="btn" onClick={onClose}>
              Back to the queue
            </button>
          </div>
        ) : null}

        {phase.kind === 'problem' ? (
          <ErrorNotice
            kind={phase.problem}
            detail={phase.detail}
            correlation_id={phase.correlation_id}
            action={
              phase.problem === 'conflict' ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    // Re-read the row from the server and close the stale panel.
                    // Nothing is patched locally: the value on screen is the one
                    // the conflict was about.
                    onSettled();
                    onClose();
                  }}
                >
                  Reload this row
                </button>
              ) : null
            }
          />
        ) : null}
      </div>
    </section>
  );
}
